<template>
  <div
    class="site-slide viewer-container relative h-full w-full bg-gray-800 overflow-hidden duration-500"
    :class="{ 'opacity-0': loading }"
    :inert="loading"
  >
    <slot name="top-bar" />
    <div ref="container" class="absolute w-full h-full" />
    <div v-if="!hideSlideTitle" class="absolute inset-0 z-20 pointer-events-none text-white">
      <div class="absolute top-8 w-full text-center text-2xl text-shadow">{{ title }}</div>
      <div v-if="siteData && siteData.attribution" class="absolute bottom-0 left-0 text-xs px-1">{{ siteData.attribution }}</div>
    </div>
    <LoadingSpinner v-if="loading" class="mt-4 items-center h-full" size="sm" />
    <div
      v-if="selectedHotspotData && hotspotType !== 'L' && hotspotType !== 'M'"
      class="w-full h-full max-h-full max-w-full absolute inset-0 flex items-center justify-center pointer-events-auto z-20"
      data-testid="site-hotspot-modal"
      :class="hotspotModalClass"
      @update:showing="clearSelectedHotspot"
    >
      <div
        v-if="hotspotType === 'N'"
        class="text-white rounded-lg max-w-2xl text-md p-6 bg-gray-900 break-words shadow-lg max-h-full overflow-auto animate-opening"
        data-testid="info-hotspot"
      >
        <h3 class="text-2xl font-normal tracking-wide text-shadow text-center mb-6" v-text="selectedHotspotData.title" />
        {{ hotspotContent }}
      </div>
      <template v-else-if="hotspotMedia?.url && hotspotType === 'P'">
        <DeckHotspotsPdfHotspot :user="user" :url="hotspotMedia.url" data-testid="pdf-hotspot" />
      </template>
      <template v-else-if="hotspotType && 'IV'.includes(hotspotType)">
        <DeckHotspotsMediaHotspot
          data-testid="media-hotspot"
          :hotspot="selectedHotspotData"
          :type="hotspotType"
          class="animate-opening"
          :dimensions="mediaHotspotDimensions"
          :is-description-open="isImageHotspotDescriptionOpen"
          @toggle-image-description="$emit('toggle-image-hotspot-description')"
        />
      </template>
      <template v-else-if="hotspotType === '3'">
        <DeckThreeDSlide
          class="h-screen p-0"
          data-testid="three-d-hotspot"
          :slide="{ model_id: threeDModelId } as DeepReadonly<Slide>"
          :uuid="uuid"
          :user="user"
          :three-d-viewer="threeDViewer"
          @frame-change="$emit('3d-frame-change')"
          @camera-ready="$emit('3d-camera-ready', $event)"
          @update:aspect="updateThreeDAnnotations('aspect', $event)"
          @update:load-status="$emit('update:3d-load-status', $event)"
          @update:animating="$emit('update:3d-animating', $event)"
        />
      </template>
    </div>
    <ClientOnly>
      <Portal to="site-back-btn">
        <DeckControlsContainer>
          <DeckControlButton class="p-3" data-testid="site-back-btn" @click="clearSelectedHotspot">
            <ArrowUturnLeftIcon class="w-6 h-6" />
          </DeckControlButton>
        </DeckControlsContainer>
      </Portal>
    </ClientOnly>
    <ClientOnly>
      <Portal to="three-d-hotspot-back-btn">
        <DeckControlsContainer>
          <DeckControlButton class="p-3" data-testid="3d-hotspot-back-btn" @click="clearSelectedHotspot">
            <ArrowUturnLeftIcon class="w-6 h-6" />
          </DeckControlButton>
        </DeckControlsContainer>
      </Portal>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import type { ComputedRef, DeepReadonly, PropType, Ref } from 'vue'
import { ArrowUturnLeftIcon } from '@heroicons/vue/24/outline'
import { once } from 'ramda'
import { degToRad } from 'three/src/math/MathUtils'

import type { Hotspot, SelectedHotspot, Site, SiteSubscriber } from '@/entities/site'
import { HotspotTypeMap } from '@/entities/site'
import type { DeckUser, Slide } from '@/entities/deck'
import type { SiteCamera } from '@/entities/siteCamera'

import type { SiteAnnotationState } from '@/composables/useSiteAnnotationState'
import { useSiteAnnotationState } from '@/composables/useSiteAnnotationState'
import type { PanZoomTransform } from '@/composables/useImagePanZoom'
import type { SiteState } from '@/composables/useDeck'

import type { HotspotRightClickEvent } from '@/lib/site-viewer/site-viewer'
import { SiteViewer, ViewEvent } from '@/lib/site-viewer/site-viewer'
import { getSite, getSiteCamera } from '@/lib/site-viewer/site-loader'
import { getFovRatio, getLookAtPoint, worldToUv } from '@/lib/site-viewer/utils'
import { DEFAULT_SITE_TWEEN_DURATION, DEFAULT_WEB_ZOOM, MAX_FOV } from '@/lib/site-viewer/constants'
import type { SiteViewerControls } from '@/lib/site-viewer/siteViewerControls'
import { ControlEvent } from '@/lib/site-viewer/siteViewerControls'

import { HostSiteControlsWithZoom } from '@/lib/site-viewer/hostSiteControlsWithZoom'
import { ParticipantSiteControls } from '@/lib/site-viewer/participantSiteControls'
import type { TileRowSize } from '@/composables/useTileRowSize'
import type { UseThreeDViewer } from '~/composables/useThreeDViewer'
import type { CameraData } from '~/entities/model3d'
import type { Booleanish } from '@/entities/index'

const props = defineProps({
  site: {
    type: Object as PropType<DeepReadonly<SiteState>>,
    required: true,
  },
  slide: {
    type: Object as PropType<DeepReadonly<Slide>>,
    required: true,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  autoRotate: {
    type: [Boolean, String],
    default: false,
  },
  user: {
    type: Object as PropType<DeepReadonly<DeckUser>>,
    default: () => ({}),
  },
  imageHotspotPanZoomTransform: {
    type: Object as PropType<PanZoomTransform>,
    default: () => ({ css: {}, dimensions: null }),
  },
  isImageHotspotDescriptionOpen: {
    type: Boolean,
    default: false,
  },
  currentPdfPage: {
    type: Number,
    default: 0,
  },
  hideSlideTitle: {
    type: [Boolean, String],
    default: false,
  },
  slideRefreshKey: {
    type: Number,
    default: 0,
  },
  uuid: {
    type: String,
    default: null,
  },
  tileRowSize: {
    type: String as PropType<TileRowSize>,
    default: 'small',
  },
  hiddenHotspotIds: {
    type: Array as PropType<(number | undefined)[]>,
    default: () => [],
  },
  clickedHotspot: {
    type: Object as PropType<Hotspot | null>,
    default: null,
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
  isEditorMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'frame-change',
  'update:annotations',
  'update:site-camera',
  'update:site-observer',
  'goto:hotspot',
  'toggle-image-hotspot-description',
  'camera-ready',
  'camera-change',
  'update-controls',
  'update:image-dimensions',
  'update:animating',
  '3d-frame-change',
  '3d-camera-ready',
  'update:3d-animating',
  'update:3d-camera-observer',
  'update:3d-load-status',
  'site-data-change',
  'set-camera',
  'select-hotspot',
  'initialized',
  'interaction',
  'contextmenu',
])

const { $analytics, $sentry } = useNuxtApp()
const container: Ref<HTMLElement | null> = ref(null)
const siteData = ref<Site | null>(null)
const selectedHotspotData: Ref<Hotspot | null> = ref(null)
const mediaHotspotDimensions = reactive({ width: 0, height: 0 })
const hotspotMedia = computed(() => selectedHotspotData.value?.media[0])
const hotspotType = ref(selectedHotspotData.value?.media[0]?.type)
const loading: Ref<Booleanish> = ref(false)
const hiddenHotspotIds = toRef(props, 'hiddenHotspotIds')
const clickedHotspot = toRef(props, 'clickedHotspot')
const title = ref(props.slide.title)
let isGoingToHotspot: boolean = false
let frameId: number | null = null
let needsRender: boolean = true
let stopEmittingCameraEvents: boolean = false
let cameraRef: SiteCamera | null = null

const route = useRoute()
const { noTween } = route.query
const tweenDuration = computed(() => (noTween ? 0 : DEFAULT_SITE_TWEEN_DURATION))

const invalidate = () => {
  needsRender = true
}
const startTween = () => emit('update:animating', true)
const stopTween = () => emit('update:animating', false)

const {
  updateSiteAnnotations: _updateSiteAnnotations,
  updateThreeDAnnotations,
  updateImageHotspotAnnotations,
  updatePdfHotspotAnnotations,
} = useSiteAnnotationState(
  computed(() => props.site.selectedHotspot?.type ?? null),
  {
    onZoomChange(zoom) {
      emit('update:annotations', { zoom })
    },
    onAspectRatioChange(aspect) {
      emit('update:annotations', { aspect })
    },
    onUvPanChange(uvCoords) {
      emit('update:annotations', { uvCoords })
    },
    onSiteCameraChange(val) {
      emit('update:site-camera', val)
    },
    onImageDimensionChange(size) {
      if (size) {
        emit('update:annotations', { aspect: size.width / size.height })
      }
      emit('update:image-dimensions', size)
    },
  },
)

type ExtSiteAnnoState = SiteAnnotationState & { fov: number }
type UpdateSiteAnnotations = <K extends keyof ExtSiteAnnoState>(key: K, value: ExtSiteAnnoState[K]) => void

const updateSiteAnnotations: UpdateSiteAnnotations = (key, value) => {
  if (key === 'fov') {
    // Convert field of view to a zoom ratio of max zoom : desired zoom
    // Clamp according to viewer spec
    let zoomFactor = value as number
    if (viewer) zoomFactor = viewer.clampFov(zoomFactor)
    zoomFactor = getFovRatio(MAX_FOV, degToRad(zoomFactor))
    return _updateSiteAnnotations('zoomFactor', zoomFactor)
  }
  return _updateSiteAnnotations(key, value)
}

const updateSiteAspectRatio = ({ payload: size }: { payload?: Size }) => {
  if (!size) return
  const aspect = size.width / size.height
  updateSiteAnnotations('aspect', aspect)
}

const updateZoom = ({ payload }: { payload?: { zoom?: number } }) => {
  if (!payload?.zoom) return

  if (!stopEmittingCameraEvents) emit('camera-change', { event: 'zoom', payload })
  updateSiteAnnotations('fov', payload.zoom)
}

const updatePan = ({ payload }: { payload?: any }) => {
  if (!stopEmittingCameraEvents) emit('camera-change', { event: 'pan', payload })
  updateSiteAnnotations('uv', payload.coordinate)
}

let abortController = new AbortController()

const threeDModelId: ComputedRef<number | undefined> = computed(() => hotspotMedia.value?.linked_to ?? undefined)

let viewer: SiteViewer | null = null
const controls: SiteViewerControls =
  props.user.deckUserType === 'controlled' ? new ParticipantSiteControls() : new HostSiteControlsWithZoom()

let currentSiteId: number | null
let currentCameraId: number | null

async function _changeSiteView(newSiteId: number | null, newCameraId: number | null, forceReset = false) {
  if (!viewer) {
    throw new Error('Site viewer failed to initialize')
  }

  if (!newSiteId) return

  emit('camera-ready', false)

  if (newSiteId !== currentSiteId) {
    await onNewSiteChange(newSiteId, newCameraId)

    // If the slide changes quickly then this could be an old/stale
    // promise. Make sure that we should be setting currentSiteId to the
    // correct one.
    if (newSiteId === props.site.siteId) {
      currentSiteId = newSiteId
      currentCameraId = newCameraId
    }
  } else if (newCameraId !== currentCameraId || forceReset) {
    // Set loading = false in case we're interrupting a model change
    loading.value = false

    // Only camera changed
    const camera = await getSiteCamera(newSiteId, newCameraId, { uuid: props.uuid })

    // Set if still relevant
    if (siteData.value && props.site.siteId === newSiteId && props.site.cameraId === newCameraId) {
      if (newCameraId !== currentCameraId) viewer.removeHotspots()

      await _setCamera(camera)

      if (newCameraId !== currentCameraId) viewer.addHotspots(_getFilteredHotspots(camera))

      currentCameraId = newCameraId
    }
  }

  emit('camera-ready', true)

  title.value = props.site.selectedHotspot && siteData.value?.title ? siteData.value?.title : props.slide.title

  invalidate()
}

function escapeKeyListener(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    clearSelectedHotspot()
  }
}

onMounted(async () => {
  if (!container.value) return

  document.addEventListener('keydown', escapeKeyListener)

  try {
    viewer = new SiteViewer(container.value)
    const { width, height } = container.value.getBoundingClientRect()

    // reported for annotations layer
    updateSiteAnnotations('camera', viewer.camera)
    updateSiteAnnotations('aspect', width / height)
    viewer.addEventListener(ViewEvent.Resize, invalidate)
    viewer.addEventListener(ViewEvent.Resize, updateSiteAspectRatio)
    viewer.addEventListener(ViewEvent.HotspotClicked, _focusAndGoToHotspot)
    viewer.addEventListener(ViewEvent.HotspotRightClicked, _openHotspotContextMenu)
    viewer.addEventListener(ViewEvent.FovClamped, ({ fov }) => {
      // Do not publish FOV when clamp is triggered since clamp is a function of the window aspect ratio
      updateSiteAnnotations('fov', fov)
    })

    controls.attach(viewer)
    controls.addEventListener(ControlEvent.Zoom, updateZoom)
    controls.addEventListener(ControlEvent.Pan, updatePan)
    controls.addEventListener(ControlEvent.ViewChangedEvent, invalidate)
    controls.addEventListener(ControlEvent.StartTween, startTween)
    controls.addEventListener(ControlEvent.StopTween, stopTween)
    controls.addEventListener(ControlEvent.InitializedCamera, _onInitializedCamera)
    controls.addEventListener(ControlEvent.PointerDown, _onInteraction)
    controls.addEventListener(ControlEvent.Wheel, _onInteraction)

    if (!props.site.siteId) return

    abortController = new AbortController()
    const mountedSignal = abortController.signal

    emit('camera-ready', false)

    onFrame()

    await onNewSiteChange(props.site.siteId, props.site.cameraId, { onMounted: true }, mountedSignal)

    if (mountedSignal.aborted) return

    if (!props.site.selectedHotspot || props.site.selectedHotspot.type !== 'L') {
      currentSiteId = props.site.siteId
      currentCameraId = props.site.cameraId
    }

    const startingWithHotspotOpen = props.site.selectedHotspot
    if (startingWithHotspotOpen) {
      const hotspot = _locateHotspotWithinSite(props.site.selectedHotspot as SelectedHotspot) // typescript doesn't really like it if you extract the if condition into a function but it's done for readability.
      if (hotspot) await _enterHotspot(hotspot)
    }

    if (mountedSignal.aborted) return

    emit('camera-ready', true)
    emit('update:site-observer', {
      pan(payload) {
        controls.lookAt({
          position: { x: payload.coordinate.x, y: 1 - payload.coordinate.y },
          tweenDuration: payload.duration,
        })
      },
      zoom(payload) {
        controls.updateZoom({ fov: payload.zoom, shouldPublish: true })
      },
    } as SiteSubscriber)

    if (props.autoRotate) {
      controls.startAutoRotate()
    }
  } catch (error) {
    $sentry?.captureException(error)
  }
})

onUnmounted(() => {
  // Reset zoom for iOS
  controls.resetZoom()

  // siteDataAxiosSource.cancel('Operation canceled by the user.')
  // siteCameraAxiosSource.cancel('Operation canceled by the user.')

  document.removeEventListener('keydown', escapeKeyListener)

  abortController.abort('Operation canceled by the user.')

  if (frameId) {
    cancelAnimationFrame(frameId)
  }

  if (viewer) {
    viewer.removeEventListener(ViewEvent.Resize, invalidate)
    viewer.removeEventListener(ViewEvent.Resize, updateSiteAspectRatio)
    viewer.removeEventListener(ViewEvent.HotspotClicked, _focusAndGoToHotspot)
    viewer.removeEventListener(ViewEvent.HotspotRightClicked, _openHotspotContextMenu)
    viewer.dispose()
  }

  controls.removeEventListener(ControlEvent.Zoom, updateZoom)
  controls.removeEventListener(ControlEvent.Pan, updatePan)
  controls.removeEventListener(ControlEvent.ViewChangedEvent, invalidate)
  controls.removeEventListener(ControlEvent.StartTween, startTween)
  controls.removeEventListener(ControlEvent.StopTween, stopTween)
  controls.removeEventListener(ControlEvent.InitializedCamera, _onInitializedCamera)
  controls.removeEventListener(ControlEvent.PointerDown, _onInteraction)
  controls.removeEventListener(ControlEvent.Wheel, _onInteraction)
  controls.detach()

  emit('update:site-observer', null)
})

const onFrame = () => {
  frameId = requestAnimationFrame(onFrame)

  controls.update()

  if (!viewer || !needsRender) {
    return
  }

  needsRender = false

  viewer.render()
}

// Handles when the site (and therefore the camera) have both changed
const onNewSiteChange = async (
  newSiteId: number,
  cameraId: number | null,
  options: { onMounted: boolean } = { onMounted: false },
  signal?: AbortSignal,
): Promise<void> => {
  if (!viewer) {
    throw new Error('Site viewer failed to initialize')
  }

  const config = { uuid: props.uuid }

  // Fetch data
  loading.value = true

  // set current state to null in case we are interrupted while loading screen is up
  currentSiteId = null
  currentCameraId = null
  const [{ site, texture }, camera] = await Promise.all([getSite(newSiteId, config), getSiteCamera(newSiteId, cameraId, config)])

  // Set if still relevant. If onMounted we can't early escape this since we need some data.
  if (props.site.siteId === newSiteId || options.onMounted) {
    siteData.value = site

    if (!viewer) return

    viewer.removeHotspots()
    viewer.setBackground(texture)

    const fov = props.site.initialState?.zoom || camera?.zoom || DEFAULT_WEB_ZOOM
    // Don't await for this since that would block the loading
    controls.updateZoom({ fov, shouldPublish: true, tweenDuration: 0.5 })

    viewer.addHotspots(_getFilteredHotspots(camera))

    const { initial_pan_x: x, initial_pan_y: y } = site
    updateSiteAnnotations('uv', { x, y })
    updateSiteAnnotations('fov', fov)
    await controls.setInitialView({ position: { x, y: 1 - y }, tweenDuration: 0 })

    loading.value = false

    if (!signal?.aborted && props.site.siteId === newSiteId && props.site.cameraId === cameraId) {
      await _setCamera(camera)
    }
  }
}

const _getFilteredHotspots = (camera: SiteCamera | null): Hotspot[] => {
  if (!siteData.value?.hotspots) return []
  let filters: { (h: Hotspot): boolean }[] = []

  if (props.user.deckUserType) {
    filters.push((h: Hotspot) => h.media?.[0]?.type !== 'V')
  }

  if (camera) {
    filters.push((h: Hotspot) => !camera.hidden_hotspots.find(({ hotspot_id }) => hotspot_id === h.id))
  }

  if (!props.user.deckUserType && !props.isAdmin) {
    filters.push((h: Hotspot) => h.status === 'P')
  }

  return siteData.value.hotspots.filter(hs => filters.every(filter => filter(hs)))
}

const _setCamera = async (siteCamera: SiteCamera | null) => {
  if (!viewer) return
  emit('set-camera', siteCamera)
  cameraRef = siteCamera

  if (siteCamera) {
    await controls.setCameraData(siteCamera, {
      currentState: props.site.initialState ?? {},
      tweenDuration: tweenDuration.value,
    })
  } else {
    // when there is no siteCamera
    if (!siteData.value) return
    let x, y

    if (props.site.initialState?.coordinate) {
      ;({ x, y } = props.site.initialState.coordinate)
    } else {
      ;({ initial_pan_x: x, initial_pan_y: y } = siteData.value)
    }
    await controls.setInitialView({ position: { x, y: 1 - y }, tweenDuration: tweenDuration.value })
  }

  _handleAnnotationCoordinates()
}

const _handleAnnotationCoordinates = () => {
  if (!viewer) return

  const { x, y } = props.site.initialState?.coordinate || {}
  const isReconnect = x && y

  let uv: Point | null = null
  if (isReconnect) {
    uv = { x, y }
  } else if (cameraRef) {
    const pt = worldToUv(getLookAtPoint({ x: cameraRef.pan_x as number, y: cameraRef.pan_y as number }))
    uv = { x: pt.x, y: 1 - pt.y }
  } else if (siteData.value) {
    uv = { x: siteData.value.initial_pan_x, y: siteData.value.initial_pan_y }
  }
  if (uv) updateSiteAnnotations('uv', uv)

  let zoom: number | null = null
  if (props.site.initialState?.zoom) {
    zoom = props.site.initialState.zoom
  } else if (isReconnect || cameraRef) {
    zoom = viewer.camera.fov
  } else if (siteData.value) {
    zoom = DEFAULT_WEB_ZOOM
  }
  if (zoom) updateSiteAnnotations('fov', zoom)
}

const _focusHotspot = async ({ hotspot, signal }: { hotspot: Hotspot; signal?: AbortSignal }): Promise<void> =>
  new Promise(resolve => {
    if (!siteData.value) return

    const resolveOnce = once(resolve)

    if (signal?.aborted) {
      resolveOnce()
      return
    }

    signal?.addEventListener('abort', () => resolveOnce(), { once: true })

    let uv = {
      x: hotspot.pos_x,
      y: 1 - hotspot.pos_y + hotspot.y_offset / siteData.value.height,
    }

    controls.lookAt({
      position: uv,
      onComplete: resolveOnce,
      tweenDuration: tweenDuration.value,
    })
  })

const _onInitializedCamera = () => {
  emit('initialized')
}

const _onInteraction = () => {
  emit('interaction')
}

watch(
  () => props.slideRefreshKey,
  async (newKey, oldKey, onCleanup) => {
    abortController.abort()
    abortController = new AbortController()
    const focusAbortController = abortController
    onCleanup(() => focusAbortController.abort())

    const slideListClicked = props.site.siteId && props.site.siteId !== currentSiteId

    if (slideListClicked) {
      // Site is a hotspot in the current site
      const hotspot = _locateHotspotWithinSite({ type: 'L', dataId: props.site.siteId as number }) // There's already a check for this value in slideListClicked but typescript can't detect it for some reason
      if (hotspot) await _focusHotspot({ hotspot, signal: focusAbortController.signal })
    }

    if (focusAbortController.signal.aborted) return

    _closeHotspot()

    return _changeSiteView(props.site.siteId, props.site.cameraId, true)
  },
)

watch(
  () => props.site.siteHistory,
  async (siteHistory, oldSiteHistory) => {
    const hasHotspotClosed = siteHistory.length < oldSiteHistory.length

    if (hasHotspotClosed) {
      _closeHotspot()

      await _changeSiteView(props.site.siteId, props.site.cameraId)
      return
    }

    if (props.site.selectedHotspot) {
      const hotspot = _locateHotspotWithinSite(props.site.selectedHotspot)

      if (hotspot) {
        stopEmittingCameraEvents = true
        try {
          await _focusHotspot({ hotspot })
        } finally {
          stopEmittingCameraEvents = false
        }

        const hotspotIsIrrelevant = props.site.selectedHotspot !== props.site.selectedHotspot
        if (hotspotIsIrrelevant) return

        await _enterHotspot(hotspot)
      }
    }
  },
  { deep: true },
)

watch(siteData, newSiteData => {
  emit('site-data-change', newSiteData)
})

watch(hiddenHotspotIds, newhiddenHotspotIds => {
  if (!viewer) return

  viewer.removeHotspots()
  const visibleHotspots = siteData.value?.hotspots.filter(hs => !newhiddenHotspotIds.includes(hs.id))
  if (visibleHotspots) {
    viewer.addHotspots(visibleHotspots)
  }

  viewer.render()
})

watch(clickedHotspot, hotspot => {
  if (!hotspot) return
  _focusHotspot({ hotspot })
})

watch(
  () => props.slide,
  newSlide => {
    title.value = newSlide.title
  },
)

function _closeHotspot() {
  selectedHotspotData.value = null
  hotspotType.value = undefined
  props.threeDViewer.world.resetResources()
}

function _locateHotspotWithinSite(selectedHotspot: SelectedHotspot): Hotspot | null {
  let hotspots = _getFilteredHotspots(cameraRef)

  switch (selectedHotspot.type) {
    case 'N':
      return hotspots.find(hotspot => hotspot.id === selectedHotspot.dataId) ?? null
    case '3':
    case 'L':
      return hotspots.find(hotspot => hotspot.media[0]?.linked_to === selectedHotspot.dataId) ?? null
    case 'P':
    case 'I':
    case 'V':
      return hotspots.find(hotspot => hotspot.media[0]?.id === selectedHotspot.dataId) ?? null
  }

  return null
}

async function _enterHotspot(hotspot: Hotspot) {
  const hotspotMedia = hotspot.media[0]
  if (hotspotMedia && hotspotMedia.type === 'L') {
    _closeHotspot()
    await _changeSiteView(hotspotMedia.linked_to ?? null, props.site.cameraId)
  } else if (hotspotMedia && hotspotMedia.type === '3') {
    try {
      loading.value = true

      const mostRecentCamera = (
        await $fetch<CameraData[]>(`v2/3dmodel/${hotspotMedia.linked_to}/cameras`, {
          ...useFetchSharedOptions(),
          params: { limit: 1, sort_by: '-updated_at' },
        })
      )[0]

      await props.threeDViewer.setActiveScene(hotspotMedia.linked_to!, mostRecentCamera?.id ?? null, { shouldTween: false })

      selectedHotspotData.value = hotspot
      hotspotType.value = hotspotMedia.type
    } catch (e) {
      throw e
    } finally {
      loading.value = false
    }
  } else {
    selectedHotspotData.value = hotspot

    hotspotType.value = hotspotMedia.type
  }
}

// This part's a little redundant while we transition the socket communication spec.
// - JN 2022-Nov-28
const _focusAndGoToHotspot = async ({ hotspot }: { hotspot: Hotspot }): Promise<void> => {
  if (!viewer || !siteData.value || isGoingToHotspot || loading.value) return

  emit('select-hotspot', hotspot)

  const currentSlideRefreshKey = props.slideRefreshKey
  isGoingToHotspot = true
  emit('camera-change', {
    event: 'pan',
    payload: {
      coordinate: { x: hotspot.pos_x, y: hotspot.pos_y - hotspot.y_offset / siteData.value.height },
      duration: DEFAULT_SITE_TWEEN_DURATION,
    },
  })

  await _focusHotspot({ hotspot })

  // Show hotspot content, if any
  $analytics.track('panoramaHotspotSelected', {
    hotspotId: hotspot.id,
    hotspotTitle: hotspot.title,
    hotspotType: hotspot.media[0].type,
  })

  let dataId: number | null
  switch (hotspot.media[0].type) {
    case '3':
    case 'L':
      dataId = hotspot.media[0].linked_to
      break
    case 'N':
      dataId = hotspot.id
      break
    default:
      dataId = hotspot.media[0]?.id
      break
  }

  const newSlideHasBeenClicked = currentSlideRefreshKey !== props.slideRefreshKey

  if (!newSlideHasBeenClicked) {
    if (!props.isEditorMode && hotspot.media[0].type === 'L') {
      loading.value = true
    }

    // Preload hotspot media
    if (hotspot.media.length) {
      if (hotspot.media[0].type === 'I') {
        let image = new Image() // Create temp image element
        await new Promise<void>(resolve => {
          image.onload = () => {
            mediaHotspotDimensions.width = image.width
            mediaHotspotDimensions.height = image.height // Set image dimensions before it needs to be drawn
            resolve()
            image.remove() // Garbage collect the temp image
          }
          image.src = String(hotspot.media[0].url) // Set the src, this will load and cache the image file
        })
      }
    }

    emit('goto:hotspot', {
      type: hotspot.media[0].type,
      siteId: hotspot.site_id,
      lookAt: { x: hotspot.pos_x, y: hotspot.pos_y - hotspot.y_offset / siteData.value.height },
      zoom: viewer?.camera.fov,
      dataId,
    })
  }

  isGoingToHotspot = false
}

function _openHotspotContextMenu(e: HotspotRightClickEvent) {
  emit('contextmenu', e.event, e.hotspot)
}
const clearSelectedHotspot = () => {
  if (props.site.selectedHotspot && props.site.selectedHotspot.type !== 'L') {
    // There is a race condition here between navigation goto:site and panorama pan events.
    // There's nothing we can do because navigation will reset the pan coordinate, but we can give it
    // a head start so that it's unlikely to occur.
    const point = controls.targetPosition
    if (!point) return
    const payload = { coordinate: { x: point.x, y: 1 - point.y }, duration: 0.5 }
    const zoom = props.site.initialState?.zoom || DEFAULT_WEB_ZOOM
    if (!stopEmittingCameraEvents) {
      setTimeout(() => {
        emit('camera-change', { event: 'pan', payload })
        emit('camera-change', { event: 'zoom', payload: { zoom } })
      }, 30)
    }
  }

  emit('goto:hotspot', null)
}

const hotspotContent = computed(() => selectedHotspotData.value?.content ?? 'Hotspot type not yet supported')
const hotspotModalClass = computed(() => {
  if (!hotspotType.value) return null
  return `${HotspotTypeMap[hotspotType.value]}-hotspot-modal ${hotspotType.value === '3' ? 'p-0' : 'p-12'}`
})
</script>

<style lang="stylus" scoped>
.model-hotspot-modal.v-modal
  :deep(.v-modal__mask)
    @apply p-0

  :deep(.v-modal__container)
    @apply rounded-none w-full h-screen

.info-hotspot-modal.v-modal, .pdf-hotspot-modal.v-modal
  :deep(.v-modal__container)
    @apply w-4/5 p-8 pt-6
    background radial-gradient(#4a4a4a, #212121)

    button svg
      @apply text-white

.image-hotspot-modal.v-modal, .video-hotspot-modal.v-modal
  :deep(.v-modal__container)
    @apply bg-transparent shadow-none

    button svg
      @apply text-white

:deep(.hotspot-layer)
  @apply absolute top-0 select-none

:deep(.hotspot-container)
  @apply flex flex-col items-center text-center opacity-75 -mt-4

  &:hover
    @apply opacity-100

:deep(.hotspot-icon)
  @apply w-8 h-8 cursor-pointer

:deep(.hotspot-label)
  @apply bg-white py-1 px-2 text-xs leading-5 mb-1 rounded-3xl cursor-default truncate
  min-width: 4.5rem
  max-width: 40ch

  &.private-hotspot
    @apply bg-gold-50

@keyframes animateOpening
  from
    transform scale(0.75)
    opacity 0

  to
    transform scale(1)
    opacity 1

.animate-opening
  animation-name: animateOpening
  animation-duration: 200ms
  animation-iteration-count: 1
  animation-timing-function: ease-out
</style>
